/** In-app feedback page (newsletter “Reply with feedback” and similar CTAs). */
export const FORMSUITE_FEEDBACK_APP_URL = 'https://app.formsuite.co/feedback';

/**
 * Default 20% upgrade offer for free-plan workspaces (sidebar callout, billing page).
 * Keep in sync with Stripe promo `WELCOMEFRIEND` in libs/server/stripe/.../promoCodes.ts
 * and PlanSelector OFFER_CODE_DISCOUNTS.
 */
export const DEFAULT_FREE_USER_OFFER_CODE = 'WELCOMEFRIEND' as const;

export function workspaceBillingPath(
  workspaceId: string,
  query?: Record<string, string | null | undefined>,
  options?: { defaultOfferCode?: string },
): string {
  const params = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value != null && value !== '') params.set(key, value);
    }
  }
  if (options?.defaultOfferCode && !params.has('offerCode')) {
    params.set('offerCode', options.defaultOfferCode);
  }
  const qs = params.toString();
  return `/workspace/${workspaceId}/settings/billing${qs ? `?${qs}` : ''}`;
}

/** Replace legacy mailto support links on feedback-style CTAs with {@link FORMSUITE_FEEDBACK_APP_URL}. */
export function normalizeNewsletterFeedbackCalloutUrl(url: string | undefined): string | undefined {
  const t = url?.trim();
  if (!t) return url;
  if (t.toLowerCase().startsWith('mailto:support@formsuite.co')) {
    return FORMSUITE_FEEDBACK_APP_URL;
  }
  return t;
}

/** New Release newsletter default author headshot — hosted on S3 for all environments. */
export const NEW_RELEASE_DEFAULT_AUTHOR_PHOTO_URL =
  'https://s3.amazonaws.com/leadrr-prod-uploads/campaigns/18913a20-813c-4819-ab60-38720dbd82c4/brady-profile-image.png';

/**
 * Email images are hosted on the SENDING domain (formsuite.co/images/email/)
 * — image URLs on a foreign host (the old S3 bucket) look suspicious to
 * receivers, particularly Gmail, and got flagged in deliverability reports.
 * Host any new email asset under the same path.
 */
const EMAIL_IMAGE_BASE = 'https://formsuite.co/images/email';

/** Header wordmark (dark) — static asset; do not reuse summary / campaign upload paths. */
export const NEW_RELEASE_EMAIL_HEADER_WORDMARK_IMG_URL =
  'https://leadrr-prod-uploads.s3.us-east-1.amazonaws.com/campaigns/18913a20-813c-4819-ab60-38720dbd82c4/formsuite-logo-black.png';

/** Footer wordmark (light on dark strip) — static asset; do not reuse summary / campaign upload paths. */
export const NEW_RELEASE_EMAIL_FOOTER_WORDMARK_IMG_URL = `${EMAIL_IMAGE_BASE}/formsuite-logo-white.png`;

/** Footer social pictograms. */
export const NEW_RELEASE_EMAIL_SOCIAL_FACEBOOK_IMG_URL = `${EMAIL_IMAGE_BASE}/facebook.png`;
export const NEW_RELEASE_EMAIL_SOCIAL_X_IMG_URL = `${EMAIL_IMAGE_BASE}/twitter.png`;
export const NEW_RELEASE_EMAIL_SOCIAL_LINKEDIN_IMG_URL = `${EMAIL_IMAGE_BASE}/linkedin.png`;

export const PRO_PRICE_ID = 'price_1PNqi3H1E5UsKynvcHNBfqP8'
// 'price_1P7w98H1E5UsKynvXQZy0L6c'

export const pricingCardDetails = [
  {
    title: 'Pro',
    classTitle: "title-purple",
    description: 'Everything you need to use Formsuite like a pro',
    // recommended: true,
    features: [
      'AI Conversations/month',
      'Remove Formsuite Badge',
      'Unlimited Workspace Seats',
    ],
    priceId: PRO_PRICE_ID,
  },
]

export const FREE_CONVERSATIONS_COUNT = 35000
export const SKIP_MESSAGE = 'Skipped'

export const OTHER_OPTION_PREFIX = 'other:';
export function stripOtherPrefix(value: string): string {
  return value.startsWith(OTHER_OPTION_PREFIX) ? value.slice(OTHER_OPTION_PREFIX.length) : value;
}

/** Strip `other:` from each comma-separated segment (multi-select / combined MC answers). */
export function stripOtherPrefixFromCommaSeparated(value: string): string {
  if (!value.includes(',')) {
    return stripOtherPrefix(value);
  }
  return value
    .split(',')
    .map((s) => stripOtherPrefix(s.trim()))
    .join(', ');
}

/**
 * Turn stored MULTIPLE_CHOICE / DROPDOWN segments (option ids or `other:…` free text) into display labels.
 * Without this, mixing real options with "other" drops the other text whenever any option id matches.
 */
export function mapStoredChoiceSegmentsToLabels(
  segments: string[],
  options: { id: string; value: string }[],
): string[] {
  return segments
    .map((segment) =>
      segment.startsWith(OTHER_OPTION_PREFIX)
        ? stripOtherPrefix(segment)
        : options.find((o) => o.id === segment)?.value,
    )
    .filter((v): v is string => v !== undefined && v !== '');
}

export const ALLOWED_RESPONSE_COUNT = 35000

import type { CalendarProvider } from '@plura/shared/types';

export interface PricingFeature {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  schedulerProviders?: CalendarProvider[];
}

export interface PricingCard {
  title: string;
  description: string;
  icon: string;
  price: {
    monthly: number;
    yearly: number;
  }
  features: {
    card: PricingFeature[];
    usage: PricingFeature[];
    core: PricingFeature[];
    branding: PricingFeature[];
    customizations: PricingFeature[];
    insight: PricingFeature[];
    support: PricingFeature[];
    sharing: PricingFeature[];
  }
}

export const plansDetails: PricingCard[] = [
  {
    title: 'Free',
    description: 'Unlimited forms and responses',
    icon: 'fa-regular fa-regular fa-cube',
    price: {
      monthly: 0,
      yearly: 0,
    },
    features: {
      card: [],
      usage: [
        {
          id: 'number-of-forms',
          title: 'Number of forms',
          description: 'Unlimited',
        },
        {
          id: 'questions-per-form',
          title: 'Questions per form',
          description: 'Unlimited',
        },
        {
          id: 'form-responses',
          title: 'Form responses',
          description: 'Unlimited',
        },
        {
          id: 'workspace-members',
          title: 'Workspace members',
          description: '1',
        },
      ],
      core: [],
      branding: [],
      customizations: [
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'ai-form-builder',
          title: 'AI Chat Assistant',
          description: '10 messages',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: '10 messages',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'upload-media',
          title: 'Upload your own media',
          description: '10MB limit',
        },
        {
          id: 'notification-emails',
          title: 'Notification emails on form submissions',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'remove-branding',
          title: 'Remove built with Formsuite badge',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'branding-logo',
          title: 'Branding logo',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'duplicate-form',
          title: 'Duplicate forms',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'custom-domains',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'advanced-ai-training',
          title: 'Advanced AI training',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic-to-forms',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'hidden-fields',
          title: 'Hidden fields',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-fonts',
          title: 'Custom fonts',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-themes',
          title: 'Custom themes',
          description: '1 included',
        },
        {
          id: 'scheduler-bookings',
          title: 'Scheduler bookings',
          schedulerProviders: ['CALENDLY', 'CALCOM'],
        },
        {
          id: 'google-calendar-scheduling',
          title: 'Google Calendar & Meet links',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'form-folders',
          title: 'Form folders',
          description: '2',
        },
        {
          id: 'custom-themes-count',
          title: 'Saved custom themes',
          description: '1',
        },
      ],
      insight: [
        {
          id: 'ai-lead-analysis',
          title: 'AI lead analysis',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'ai-form-analysis',
          title: 'AI form analysis & key insights',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'lead-summaries',
          title: 'Response summaries',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'reports',
          title: 'Reports',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'download-responses',
          title: 'Download responses',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-form-metrics',
          title: 'Advanced form metrics',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'workspace-metrics',
          title: 'Workspace metrics',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      support: [],
      sharing: [
        {
          id: 'customized-end-screen',
          title: 'Customized end screen',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'link-to-url',
          title: 'Link to URL on end screen',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'embed-forms',
          title: 'Embed forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'popup-forms',
          title: 'Popup forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'banner-forms',
          title: 'Banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains-count',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-x',
        },
      ],
    },
  },
  {
    title: 'Pro',
    description: 'Elevate your forms to reflect your brand’s identity',
    icon: 'fa-regular fa-solid fa-cube',
    price: {
      monthly: 24,
      yearly: Math.ceil(216 / 12),
    },
    features: {
      card: [
        {
          id: 'ai-form-builder',
          title: 'Unlimited AI Chat Assistant',
          description: 'Build, design, and refine forms through a natural chat conversation — just describe what you want and the AI handles the rest',
          icon: 'fa-regular fa-wand-magic-sparkles',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: 'Get instant answers, tips, and walkthroughs for any feature on the platform, available 24/7',
          icon: 'fa-regular fa-headset',
        },
        {
          id: 'branding',
          title: 'Remove Formsuite branding',
          description: 'Hide the "built with Formsuite" badge and all Formsuite branding',
          icon: 'fa-regular fa-pencil',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          description: 'Conversational blocks allow AI to ask and answer questions with respondents',
          icon: 'fa-regular fa-comment',
        },
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          description: 'Collect and save form responses before they are finished.',
          icon: 'fa-regular fa-table',
        },
        {
          id: 'ai-summary',
          title: 'Response summaries',
          description: 'Easliy understand your responses with AI summaries',
          icon: 'fa-regular fa-bolt',
        },
        {
          id: 'collab',
          title: 'Built for teams',
          description: 'Invite up to 5 team members to your workspace',
          icon: 'fa-regular fa-user',
        },
        {
          id: 'folders-themes',
          title: 'Folders & saved themes',
          description: 'Organize forms into up to 5 folders and reuse up to 5 saved brand themes across them',
          icon: 'fa-regular fa-folder',
        },
        {
          id: 'duplicate-forms',
          title: 'Duplicate forms',
          description: 'Make a one-click copy of any form to spin up variations or templates of your best performers',
          icon: 'fa-regular fa-copy',
        },
        {
          id: 'notification',
          title: 'Get notified of responses',
          description: 'Recieve email notifications when a form has engagement',
          icon: 'fa-regular fa-paper-plane',
        },
        {
          id: 'unlimited-uploads',
          title: 'Unlimited uploads',
          description: 'Remove the the 10MB limit for file uploads',
          icon: 'fa-regular fa-upload',
        },
        {
          id: 'advanced-analytics',
          title: 'Advanced analytics',
          description: 'Get additional insights into your responses with advanced analytics',
          icon: 'fa-regular fa-chart-line',
        },
        {
          id: 'reports',
          title: 'Reports & public reports',
          icon: 'fa-regular fa-file-chart-column',
          description: 'Create and share AI insights and detailed reports with your team using shared links',
        },
        {
          id: 'key-insights',
          title: 'Advanced key insights',
          icon: 'fa-regular fa-brain',
          description: 'Easily understand your responses and audience with generated AI key insights',
        },
        {
          id: 'scheduler',
          title: 'Scheduler bookings',
          description:
            'Let respondents book without leaving your form. Integrates with Google Calendar, Cal.com, Calendly, and your existing workflows.',
          icon: 'fa-regular fa-calendar',
        },
      ],
      usage: [
        {
          id: 'number-of-forms',
          title: 'Number of forms',
          description: 'Unlimited',
        },
        {
          id: 'questions-per-form',
          title: 'Questions per form',
          description: 'Unlimited',
        },
        {
          id: 'form-responses',
          title: 'Form responses',
          description: 'Unlimited',
        },
        {
          id: 'workspace-members',
          title: 'Workspace members',
          description: '5',
        },
      ],
      core: [],
      branding: [],
      customizations: [
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-form-builder',
          title: 'AI Chat Assistant',
          description: 'Unlimited',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: 'Unlimited',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'upload-media',
          title: 'Upload your own media',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'notification-emails',
          title: 'Notification emails on form submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'remove-branding',
          title: 'Remove built with Formsuite badge',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'branding-logo',
          title: 'Branding logo',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'duplicate-form',
          title: 'Duplicate forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-ai-training',
          title: 'Advanced AI training',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic-to-forms',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'hidden-fields',
          title: 'Hidden fields',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-fonts',
          title: 'Custom fonts',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-themes',
          title: 'Custom themes',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'scheduler-bookings',
          title: 'Scheduler bookings',
          schedulerProviders: ['CALENDLY', 'CALCOM', 'GOOGLE_CALENDAR'],
        },
        {
          id: 'google-calendar-scheduling',
          title: 'Google Calendar & Meet links',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'form-folders',
          title: 'Form folders',
          description: '5',
        },
        {
          id: 'custom-themes-count',
          title: 'Saved custom themes',
          description: '5',
        },
      ],
      insight: [
        {
          id: 'ai-lead-analysis',
          title: 'AI lead analysis',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-form-analysis',
          title: 'AI form analysis & key insights',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'lead-summaries',
          title: 'Response summaries',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'reports',
          title: 'Reports',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'download-responses',
          title: 'Download responses',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-form-metrics',
          title: 'Advanced form metrics',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'workspace-metrics',
          title: 'Workspace metrics',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      support: [],
      sharing: [
        {
          id: 'customized-end-screen',
          title: 'Customized end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'link-to-url',
          title: 'Link to URL on end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'embed-forms',
          title: 'Embed forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'popup-forms',
          title: 'Popup forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'banner-forms',
          title: 'Banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains-count',
          title: 'Custom domains',
          description: '1',
        },
      ],
    },
  },
  {
    title: 'Business',
    description: 'Unlock unlimited potential with advanced features and support',
    icon: 'fa-regular fa-solid fa-cubes',
    price: {
      monthly: 69,
      yearly: Math.ceil(624 / 12),
    },
      features: {
        card: [
          {
            id: 'included-in-pro',
            title: 'Everything in Pro',
            description: 'Everything in Pro, plus additional tools for teams who need advanced features',
            icon: 'fa-regular fa-badge-check',
          },
          {
            id: 'custom-domains',
            title: 'Unlimited custom domains',
            description: 'Run as many client or brand domains as you need (forms.yourdomain.com). Pro includes one; Business is unlimited.',
            icon: 'fa-regular fa-globe',
          },
          {
            id: 'unlimited-folders',
            title: 'Unlimited folders',
            description: 'Organize your forms across as many folders as you need without hitting plan caps.',
            icon: 'fa-regular fa-folder',
          },
          {
            id: 'unlimited-themes',
            title: 'Unlimited saved themes',
            description: 'Save unlimited reusable themes so larger teams can keep every form on-brand.',
            icon: 'fa-regular fa-palette',
          },
          {
            id: 'priority-support',
            title: 'Priority support',
            description: 'We hear for every request and will do our best to implement it',
            icon: 'fa-regular fa-headset',
          },
        ],
      usage: [
        {
          id: 'number-of-forms',
          title: 'Number of forms',
          description: 'Unlimited',
        },
        {
          id: 'questions-per-form',
          title: 'Questions per form',
          description: 'Unlimited',
        },
        {
          id: 'form-responses',
          title: 'Form responses',
          description: 'Unlimited',
        },
        {
          id: 'workspace-members',
          title: 'Workspace members',
          description: 'Unlimited',
        },
      ],
      core: [],
      branding: [],
      customizations: [
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-form-builder',
          title: 'AI Chat Assistant',
          description: 'Unlimited',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: 'Unlimited',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'upload-media',
          title: 'Upload your own media',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'notification-emails',
          title: 'Notification emails on form submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'remove-branding',
          title: 'Remove built with Formsuite badge',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'branding-logo',
          title: 'Branding logo',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'duplicate-form',
          title: 'Duplicate forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-ai-training',
          title: 'Advanced AI training',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic-to-forms',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'hidden-fields',
          title: 'Hidden fields',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-fonts',
          title: 'Custom fonts',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-themes',
          title: 'Custom themes',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'scheduler-bookings',
          title: 'Scheduler bookings',
          schedulerProviders: ['CALENDLY', 'CALCOM', 'GOOGLE_CALENDAR'],
        },
        {
          id: 'google-calendar-scheduling',
          title: 'Google Calendar & Meet links',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'form-folders',
          title: 'Form folders',
          description: 'Unlimited',
        },
        {
          id: 'custom-themes-count',
          title: 'Saved custom themes',
          description: 'Unlimited',
        },
      ],
      insight: [
        {
          id: 'ai-lead-analysis',
          title: 'AI lead analysis',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-form-analysis',
          title: 'AI form analysis & key insights',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'lead-summaries',
          title: 'Response summaries',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'reports',
          title: 'Reports',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'download-responses',
          title: 'Download responses',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-form-metrics',
          title: 'Advanced form metrics',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'workspace-metrics',
          title: 'Workspace metrics',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      support: [],
      sharing: [
        {
          id: 'customized-end-screen',
          title: 'Customized end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'link-to-url',
          title: 'Link to URL on end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'embed-forms',
          title: 'Embed forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'popup-forms',
          title: 'Popup forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'banner-forms',
          title: 'Banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains-count',
          title: 'Custom domains',
          description: 'Unlimited',
        },
      ],
    },
  },
];

export const defaultOverview = 'This is a form to collect leads'
export const defaultAbout = `We're looking to connect with new leads`
export const metaDescription = 'Transform lead collections into engaging experiences with Formsuite. Construct stunning online forms, surveys, quizzes, and much more. Experience it for FREE.'

export const HIDDEN_DATA_DUE_TO_GDPR_NO_CONSENT = 'HIDDEN_DATA_DUE_TO_GDPR_NO_CONSENT';
export const HIDDEN_GDPR_CONTENT_MESSAGE = 'Hidden due to lack of user consent under GDPR'